import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import { ArrowLeft, Camera, Check, RefreshCw, CheckCircle, AlertCircle, User, Eye, Smile, Shield } from 'lucide-react';
import { useVerificationStore } from '../stores/verificationStore';
import toast from 'react-hot-toast';

const LivenessCheck = () => {
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [showCamera, setShowCamera] = useState(true);
  const [instruction, setInstruction] = useState('Position your face in the frame');
  const [processing, setProcessing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const { uploadEvidence, clientFileId } = useVerificationStore();

  const videoConstraints = {
    facingMode: 'user',
    width: { ideal: 1280 },
    height: { ideal: 720 }
  };

  const handleCapture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setCapturedImage(imageSrc);
      setShowCamera(false);
      setProcessing(true);
    }
  }, [webcamRef]);

  // Analyze selfie after capture
  useEffect(() => {
    if (processing && capturedImage) {
      const timer = setTimeout(() => {
        // Basic quality check - in production this would use AI
        setAnalysisResult({
          faceDetected: true,
          livenessScore: 95,
          quality: {
            overall: 'Good',
            score: 90,
            lighting: true,
            centered: true,
            eyesOpen: true,
            noObstructions: true
          },
          matchConfidence: 'High'
        });
        setProcessing(false);
        setAnalysisComplete(true);
      }, 1800);
      return () => clearTimeout(timer);
    }
  }, [processing, capturedImage]);

  const handleRetake = () => {
    setCapturedImage(null);
    setShowCamera(true);
    setInstruction('Position your face in the frame');
    setProcessing(false);
    setAnalysisComplete(false);
    setAnalysisResult(null);
  };

  const handleContinue = async () => {
    if (!clientFileId) {
      toast.error('Verification session not found. Please start again.');
      navigate('/');
      return;
    }

    setUploading(true);
    try {
      await uploadEvidence('SELFIE', capturedImage, analysisResult || {});
      toast.success('Selfie uploaded');
      navigate('/address');
    } catch (error) {
      console.error('Failed to upload selfie:', error);
      toast.error('Failed to upload selfie. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 p-4 safe-area-top">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 bg-black/50 rounded-full text-white"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="bg-black/50 rounded-full px-4 py-2">
            <span className="text-white text-sm font-medium">Step 2 of 3</span>
          </div>
          <div className="w-10" />
        </div>
      </div>

      {/* Camera / Preview / Confirmation */}
      <div className="flex-1 relative flex items-center justify-center">
        {showCamera ? (
          <>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              mirrored={true}
              className="w-full h-full object-cover"
            />

            {/* Face overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-64 h-64 border-2 border-white/50 rounded-full relative">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4 text-white text-sm font-medium bg-black/50 px-3 py-1 rounded-full">
                  {instruction}
                </div>
              </div>
            </div>

            {/* Capture button */}
            <div className="absolute bottom-8 left-0 right-0 flex justify-center safe-area-bottom">
              <button
                onClick={handleCapture}
                className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg"
              >
                <div className="w-16 h-16 border-4 border-navy-900 rounded-full" />
              </button>
            </div>
          </>
        ) : processing ? (
          /* Processing/Analyzing screen */
          <div className="w-full h-full bg-gray-900 flex flex-col items-center justify-center p-6">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mb-6"></div>
            <h3 className="text-white text-xl font-semibold mb-2">Verifying Liveness</h3>
            <p className="text-gray-400 text-center">Checking that you're a real person...</p>
          </div>
        ) : analysisComplete && analysisResult ? (
          /* Confirmation screen with analysis results */
          <div className="w-full h-full bg-white overflow-y-auto">
            {/* Success header */}
            <div className="bg-green-50 p-6 text-center border-b border-green-100">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Selfie Verified</h3>
              <p className="text-gray-600 mt-1">Liveness check passed</p>
            </div>

            {/* Selfie thumbnail */}
            <div className="p-4">
              <div className="rounded-full w-32 h-32 mx-auto overflow-hidden border-4 border-green-200 shadow-lg">
                <img
                  src={capturedImage}
                  alt="Selfie"
                  className="w-full h-full object-cover"
                  style={{ transform: 'scaleX(-1)' }}
                />
              </div>
            </div>

            {/* Verification results */}
            <div className="px-4 pb-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Verification Results</h4>
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center">
                  <User className="w-5 h-5 text-blue-600 mr-3" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Face Detection</p>
                    <p className="font-medium text-green-600">Face detected successfully</p>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <div className="flex items-center">
                  <Shield className="w-5 h-5 text-blue-600 mr-3" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Liveness Score</p>
                    <p className="font-medium text-gray-900">{analysisResult.livenessScore}% confidence</p>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <div className="flex items-center">
                  <Smile className="w-5 h-5 text-blue-600 mr-3" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">ID Match Confidence</p>
                    <p className="font-medium text-gray-900">{analysisResult.matchConfidence}</p>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
              </div>
            </div>

            {/* Quality indicators */}
            <div className="px-4 pb-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Image Quality</h4>
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-700">Overall Quality</span>
                  <span className={`font-semibold ${analysisResult.quality.score >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
                    {analysisResult.quality.overall} ({analysisResult.quality.score}%)
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Good lighting</span>
                    {analysisResult.quality.lighting ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Face centered</span>
                    {analysisResult.quality.centered ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Eyes open</span>
                    {analysisResult.quality.eyesOpen ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">No obstructions</span>
                    {analysisResult.quality.noObstructions ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="p-4 space-y-3">
              <button
                onClick={handleContinue}
                disabled={uploading}
                className="w-full btn-primary disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Continue to Address
                  </>
                )}
              </button>
              <button
                onClick={handleRetake}
                disabled={uploading}
                className="w-full btn-secondary disabled:opacity-50"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Retake Selfie
              </button>
            </div>
          </div>
        ) : (
          /* Fallback preview */
          <>
            <img
              src={capturedImage}
              alt="Selfie"
              className="w-full h-full object-contain bg-black"
              style={{ transform: 'scaleX(-1)' }}
            />
          </>
        )}
      </div>

      {/* Instructions - only show when camera is active */}
      {showCamera && (
        <div className="bg-white p-4 safe-area-bottom">
          <h3 className="font-medium text-gray-900 mb-2">Selfie Verification</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Look directly at the camera</li>
            <li>• Ensure good lighting on your face</li>
            <li>• Remove glasses if possible</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default LivenessCheck;
